import React, { useState, useEffect } from "react";
import axios from "axios";
import { Modal, Button } from "react-bootstrap";
import "./DailyPurchases.css";

const FacturesAchatEmplacement = () => {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtres
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");

  // Modale d'impression / Aperçu Facture
  const [activeInvoice, setActiveInvoice] = useState(null);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedLocation !== "all") params.locationName = selectedLocation;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      let locList = [];
      try {
        const locRes = await axios.get("/api/Locations/getAllLocations");
        locList = Array.isArray(locRes.data) ? locRes.data : locRes.data.locations || [];
      } catch (e) {
        const locRes2 = await axios.get("/api/Locations");
        locList = Array.isArray(locRes2.data) ? locRes2.data : locRes2.data.locations || [];
      }
      setLocations(locList);

      const invRes = await axios.get("/api/daily-purchases/invoices-by-location", { params });
      setInvoices(invRes.data.invoices || []);
      setSummary(invRes.data.summary || []);
    } catch (err) {
      console.error("Erreur chargement factures par emplacement:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [selectedLocation, startDate, endDate]);

  const handlePrint = () => {
    window.print();
  };

  const filteredInvoices = invoices.filter((inv) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (
      inv.reference?.toLowerCase().includes(term) ||
      inv.locationName?.toLowerCase().includes(term) ||
      inv.invoiceNumber?.toLowerCase().includes(term) ||
      inv.supplierGlobalName?.toLowerCase().includes(term) ||
      inv.items?.some((it) => it.articleName.toLowerCase().includes(term))
    );
  });

  const totalGlobalAmount = filteredInvoices.reduce((sum, inv) => sum + (inv.totalActualAmount || 0), 0);
  const totalGlobalItems = filteredInvoices.reduce((sum, inv) => sum + (inv.totalPurchasedQuantity || 0), 0);

  return (
    <div className="dp-card">
      <div className="dp-card-header no-print">
        <div>
          <h3 className="dp-card-title">
            📑 Factures d'Achat & Bons d'Approvisionnement par Emplacement
          </h3>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: "0.88rem" }}>
            Historique officiel des factures et bons d'achat validés par le contrôleur pour chaque emplacement.
          </p>
        </div>

        {/* Filtres */}
        <div className="dp-filters-bar">
          <select
            className="dp-filter-select"
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
          >
            <option value="all">Tous les emplacements</option>
            {locations.map((loc, idx) => {
              const locName = typeof loc === "string" ? loc : (loc.name || loc.localisation || loc._id);
              const key = (typeof loc === "object" && loc._id) ? loc._id : `${locName}_${idx}`;
              return (
                <option key={key} value={locName}>
                  🏪 {locName}
                </option>
              );
            })}
          </select>

          <input
            type="date"
            className="dp-filter-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            title="Date de début"
          />

          <input
            type="date"
            className="dp-filter-input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            title="Date de fin"
          />

          <input
            type="text"
            className="dp-filter-input"
            placeholder="🔍 N° facture, réf..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <button className="dp-btn dp-btn-secondary" onClick={fetchInvoices}>
            🔄
          </button>
        </div>
      </div>

      {/* Cartes de Synthèse par Emplacement */}
      <div className="dp-stats-grid no-print">
        <div className="dp-stat-card">
          <div className="dp-stat-icon-wrapper" style={{ background: "#eff6ff", color: "#2563eb" }}>
            📄
          </div>
          <div className="dp-stat-info">
            <h4>Total Factures Validées</h4>
            <div className="dp-stat-val">{filteredInvoices.length}</div>
          </div>
        </div>

        <div className="dp-stat-card">
          <div className="dp-stat-icon-wrapper" style={{ background: "#ecfdf5", color: "#059669" }}>
            💰
          </div>
          <div className="dp-stat-info">
            <h4>Dépenses Achats Totales</h4>
            <div className="dp-stat-val" style={{ color: "#059669" }}>
              {totalGlobalAmount.toFixed(3)} DT
            </div>
          </div>
        </div>

        <div className="dp-stat-card">
          <div className="dp-stat-icon-wrapper" style={{ background: "#fef3c7", color: "#d97706" }}>
            📦
          </div>
          <div className="dp-stat-info">
            <h4>Articles Approvisionnés</h4>
            <div className="dp-stat-val">{totalGlobalItems} unités</div>
          </div>
        </div>
      </div>

      {/* Tableau des Factures */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-2 font-weight-bold">Chargement des factures...</p>
        </div>
      ) : filteredInvoices.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>
          <span style={{ fontSize: "3rem" }}>📑</span>
          <h5>Aucune facture trouvée</h5>
          <p>Aucun achat validé pour les critères sélectionnés.</p>
        </div>
      ) : (
        <div className="dp-table-container no-print">
          <table className="dp-table">
            <thead>
              <tr>
                <th>N° Réf / Facture</th>
                <th>Date Validation</th>
                <th>Emplacement</th>
                <th>Fournisseur / Paiement</th>
                <th>Contrôleur</th>
                <th style={{ textAlign: "center" }}>Articles</th>
                <th style={{ textAlign: "right" }}>Total TTC (DT)</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map((inv) => (
                <tr key={inv._id}>
                  <td>
                    <div style={{ fontWeight: "800", color: "#0f172a" }}>
                      {inv.reference}
                    </div>
                    {inv.invoiceNumber && (
                      <div style={{ fontSize: "0.78rem", color: "#2563eb", fontWeight: "700" }}>
                        Doc: {inv.invoiceNumber}
                      </div>
                    )}
                  </td>

                  <td>
                    <span style={{ fontWeight: "600", fontSize: "0.85rem" }}>
                      {new Date(inv.validatedAt || inv.createdAt).toLocaleDateString("fr-FR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric"
                      })}
                    </span>
                  </td>

                  <td>
                    <span
                      style={{
                        fontWeight: "800",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        background: "#f1f5f9",
                        color: "#1e293b"
                      }}
                    >
                      🏪 {inv.locationName}
                    </span>
                  </td>

                  <td>
                    <div style={{ fontWeight: "700" }}>
                      {inv.supplierGlobalName || "Divers Fournisseurs"}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                      Règlement : {inv.paymentMethod || "Espece"}
                    </div>
                  </td>

                  <td>
                    <span style={{ fontSize: "0.85rem", color: "#475569" }}>
                      👤 {inv.controllerName || "Contrôleur"}
                    </span>
                  </td>

                  <td style={{ textAlign: "center" }}>
                    <span style={{ fontWeight: "800" }}>
                      {inv.totalPurchasedQuantity || inv.items?.length || 0}
                    </span>
                  </td>

                  <td style={{ textAlign: "right" }}>
                    <span style={{ fontWeight: "900", color: "#15803d", fontSize: "1.05rem" }}>
                      {(inv.totalActualAmount || 0).toFixed(3)} DT
                    </span>
                  </td>

                  <td style={{ textAlign: "right" }}>
                    <button
                      className="dp-btn dp-btn-primary"
                      style={{ padding: "6px 14px", fontSize: "0.82rem" }}
                      onClick={() => setActiveInvoice(inv)}
                    >
                      🖨️ Facture / Bon d'Achat
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── MODALE / VUE IMPRIMABLE FACTURE PAR EMPLACEMENT ── */}
      <Modal
        show={!!activeInvoice}
        onHide={() => setActiveInvoice(null)}
        size="xl"
        dialogClassName="dp-modal-screen-width"
        centered
      >
        <Modal.Header closeButton className="no-print">
          <Modal.Title style={{ fontWeight: "800" }}>
            📄 Facture & Bon d'Achat : {activeInvoice?.reference}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "10px" }}>
          {activeInvoice && (
            <div className="dp-invoice-paper">
              {/* En-tête de la facture */}
              <div className="dp-invoice-header">
                <div className="dp-invoice-brand">
                  <h3>TASKO POS — ACHAT QUOTIDIEN</h3>
                  <p>Système de gestion des approvisionnements & caisse d'achat</p>
                </div>
                <div className="dp-invoice-meta">
                  <div className="dp-invoice-number">{activeInvoice.reference}</div>
                  <div className="dp-invoice-date">
                    Date :{" "}
                    {new Date(activeInvoice.validatedAt || activeInvoice.createdAt).toLocaleDateString(
                      "fr-FR",
                      { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }
                    )}
                  </div>
                  {activeInvoice.invoiceNumber && (
                    <div style={{ fontSize: "0.85rem", fontWeight: "700", color: "#475569" }}>
                      Réf Facture Fournisseur : {activeInvoice.invoiceNumber}
                    </div>
                  )}
                </div>
              </div>

              {/* Détails Emplacement & Règlement */}
              <div className="dp-invoice-details-grid">
                <div>
                  <div className="dp-invoice-box-title">📍 Emplacement Bénéficiaire</div>
                  <div className="dp-invoice-box-value">{activeInvoice.locationName}</div>
                  <div style={{ fontSize: "0.85rem", color: "#64748b", marginTop: "4px" }}>
                    Demandé par : {activeInvoice.requestedByName}
                  </div>
                </div>

                <div>
                  <div className="dp-invoice-box-title">🚚 Fournisseur & Paiement</div>
                  <div className="dp-invoice-box-value">
                    {activeInvoice.supplierGlobalName || "Fournisseur Comptant"}
                  </div>
                  <div style={{ fontSize: "0.85rem", color: "#64748b", marginTop: "4px" }}>
                    Mode : {activeInvoice.paymentMethod} • Visa Contrôleur : {activeInvoice.controllerName}
                  </div>
                </div>
              </div>

              {/* Tableau des articles achetés */}
              <table className="dp-table" style={{ border: "1px solid #cbd5e1", marginBottom: "20px" }}>
                <thead>
                  <tr style={{ background: "#f1f5f9" }}>
                    <th style={{ width: "40px" }}>#</th>
                    <th>Article / Désignation</th>
                    <th>Famille</th>
                    <th style={{ textAlign: "center" }}>Quantité</th>
                    <th style={{ textAlign: "right" }}>Prix Unit. (DT)</th>
                    <th style={{ textAlign: "right" }}>Montant Total (DT)</th>
                  </tr>
                </thead>
                <tbody>
                  {activeInvoice.items?.map((it, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>
                        <strong>{it.articleName}</strong>
                        {it.notes && (
                          <div style={{ fontSize: "0.75rem", color: "#64748b" }}>{it.notes}</div>
                        )}
                      </td>
                      <td>{it.categoryName}</td>
                      <td style={{ textAlign: "center", fontWeight: "700" }}>
                        {it.purchasedQuantity > 0 ? it.purchasedQuantity : it.requestedQuantity} {it.unit}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {(it.actualUnitPrice || it.estimatedUnitPrice || 0).toFixed(3)}
                      </td>
                      <td style={{ textAlign: "right", fontWeight: "800" }}>
                        {(
                          it.actualTotalPrice ||
                          (it.purchasedQuantity || it.requestedQuantity) *
                            (it.actualUnitPrice || it.estimatedUnitPrice || 0)
                        ).toFixed(3)}{" "}
                        DT
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: "#f8fafc", borderTop: "2px solid #0f172a" }}>
                    <td colSpan="5" style={{ textAlign: "right", fontWeight: "800", fontSize: "1.1rem" }}>
                      TOTAL ACHATS TTC :
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        fontWeight: "900",
                        fontSize: "1.25rem",
                        color: "#15803d"
                      }}
                    >
                      {(activeInvoice.totalActualAmount || 0).toFixed(3)} DT
                    </td>
                  </tr>
                </tfoot>
              </table>

              {/* Observations & Signatures */}
              {activeInvoice.controllerNotes && (
                <div style={{ background: "#f8fafc", padding: "10px 14px", borderRadius: "8px", fontSize: "0.85rem", marginBottom: "16px" }}>
                  <strong>Observations Contrôleur :</strong> {activeInvoice.controllerNotes}
                </div>
              )}

              <div className="dp-invoice-footer">
                <div className="dp-signature-box">
                  <span>Visa Responsable Emplacement</span>
                  <div style={{ borderBottom: "1px solid #cbd5e1" }}></div>
                  <span>Nom & Signature</span>
                </div>

                <div className="dp-signature-box">
                  <span>Visa Contrôleur d'Achats</span>
                  <div style={{ borderBottom: "1px solid #cbd5e1" }}></div>
                  <span>{activeInvoice.controllerName || "Contrôleur"}</span>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="no-print">
          <Button variant="secondary" onClick={() => setActiveInvoice(null)}>
            Fermer
          </Button>
          <Button variant="primary" style={{ fontWeight: "800" }} onClick={handlePrint}>
            🖨️ Imprimer la Facture
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FacturesAchatEmplacement;
